import { ldInit, useLDClient } from 'launchdarkly-vue-client-sdk'
import type { Ref } from 'vue'
import { useCookie } from 'nuxt/app'
import { Shopify } from '@jackboxgames/shopify'
import type { RouteLocationGeneric } from 'vue-router'
import { CookieYes } from '$services/cookieyes'
import Category = CookieYes.Category

export namespace LaunchDarkly {
    export const Metric = {
        AddToCart: 'add-to-cart',
        CarouselSlide: 'homepage-carousel-slide-shown',
        Checkout: 'initiate-checkout',
        CheckoutRevenue: 'checkout-initiated-revenue',
        ExternalStorefront: 'storefront-cta',
        SaleGridTileClick: 'sale-grid-tile-click',
        WishlistClickthrough: 'wishlist-cta',
        WishlistClickthroughDesktop: 'desktop-wishlist-cta',
        SaleButtonNav: 'sale-button-nav-cta'
    }

    export interface InitOptions {
        id: string
    }

    export class LaunchDarklyPlugin {
        private readonly id: string // JBG_LISTRAK_ID
        private client: any
        private ready: Ref<boolean> = ref(false)

        constructor(options: LaunchDarkly.InitOptions) {
            this.id = options.id
        }

        // Call setup once in layouts/default.vue
        public setup(route: RouteLocationGeneric) {
            if (!this.isReady()) {
                const cookieConsentAnalytics = CookieYes.hasConsent(Category.Analytics)
                if (cookieConsentAnalytics) {
                    const clientSideID = this.id
                    const context = this.buildUserContext(route)

                    if (clientSideID) {
                        try {
                            ldInit({ clientSideID, context })
                            this.client = useLDClient()
                            this.ready.value = true
                        } catch (e) {
                            console.error(e)
                        }
                    }
                }
            }
        }

        public isReady(): boolean {
            return this.ready && this.ready.value
        }

        public getUuid(): string | null {
            const cookie = useCookie('jbg_id')
            return cookie.value ?? null
        }

        private buildUserContext(route: RouteLocationGeneric): any {
            const cookie = useCookie('jbg_id')
            const uuid = cookie.value

            const context = {
                kind: 'user',
                key: null,
                anonymous: false,
                pageType: this.getPageType(route) || null
            }
            if (uuid) {
                context.key = uuid
            } else {
                context.anonymous = true
            }

            return context
        }

        public track(metric: string, attributes?: any, metricValue?: number) {
            if (this.isReady()) {
                const route = useRoute()
                this.client.track(metric, {
                    path: route.path,
                    params: route.query,
                    ...attributes
                }, metricValue)
            }
        }

        public trackStorefrontClick(productSlug: string, platform: string, product: Shopify.Product) {
            this.track(Metric.ExternalStorefront, { productSlug, platform, product })
        }

        public trackSaleGridTileClick(
            productSlug: string,
            platform: string,
            price: string,
            compareAtPrice: number
        ) {
            this.track(Metric.SaleGridTileClick, {
                productSlug, platform, price, compareAtPrice
            })
        }

        public useFlag(key: string, defaultVariant: any): Ref<any> {
            const variant = ref(defaultVariant)

            // Subscribes the returned ref directly to the LaunchDarkly client, instead of going
            // through useLDFlag(), so it still works correctly even if LaunchDarkly isn't ready
            // yet when this is called. Without this, a flag read too early would get stuck on
            // its default value forever, since nothing would ever come back and update it once
            // the real value was ready. Listens for both `change:${key}` (later updates) and
            // `ready` (the initial load, which doesn't reliably emit a `change` event) so the
            // value is never missed.
            const bindToClient = () => {
                const client = this.client
                variant.value = client.variation(key, defaultVariant)
                client.on(`change:${key}`, (newValue: any) => {
                    variant.value = newValue
                })
                client.on('ready', () => {
                    variant.value = client.variation(key, variant.value)
                })
            }

            if (this.isReady()) {
                bindToClient()
            } else {
                const stopWatch = watch(this.ready, (ready) => {
                    if (ready) {
                        bindToClient()
                        stopWatch()
                    }
                })
            }

            return variant
        }

        // this is "complex" because of all the path-matching we have to do, and there's not much we can do about it
        // eslint-disable-next-line complexity
        private getPageType(route: RouteLocationGeneric) {
            const name = route.name as string

            if (!name) {
                return ''
            }

            // TODO: eventually we might want to use the route.path / route.fullPath data
            //  to identify specific products (games/packs/merch/etc.)

            // TODO: add more pages here as (if) we need them
            // We match using `startsWith` instead of an exact/equality match due to localization.
            // For example, the full pack page name for English is `games-packs-packslug__en`.
            if (name.startsWith('index')) {
                return 'home'
            } if (name.startsWith('games-packs-packslug-gameslug')) {
                return 'game_pdp'
            } if (name.startsWith('games-packs-packslug')) {
                return 'pack_pdp'
            } if (name.startsWith('games-packs')) {
                return 'packs_index'
            } if (name.startsWith('games')) {
                return 'games_index'
            } if (name.startsWith('sale')) {
                return 'on_sale'
            } if (name.startsWith('blog-postslug')) {
                return 'blog_post'
            } if (name.startsWith('blog')) {
                return 'blog_index'
            } if (name.startsWith('how-to-play')) {
                return 'how-to-play'
            } if (name.startsWith('shop-tab')) {
                return 'shop'
            }

            return ''
        }

        public updateContext(route: RouteLocationGeneric) {
            if (this.isReady()) {
                let context = this.client.getContext()
                if (!context) {
                    context = this.buildUserContext(route)
                }

                context.pageType = this.getPageType(route)

                // the "null" parameter below would be a hash of the context key if we were running in "secure mode"
                this.client.identify(context, null, () => {})
            }
        }
    }

    export function install(key: string) {
        return new LaunchDarklyPlugin({ id: key })
    }

}
